(() => {
  if (window.DaramelCardSlider) {
    window.DaramelCardSlider.init(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CardSlider {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-slider-viewport]');
      this.track = section.querySelector('[data-slider-track]');
      this.slides = Array.from(section.querySelectorAll('[data-slider-slide]'));
      this.controls = section.querySelector('[data-slider-controls]');
      this.previousButton = section.querySelector('[data-slider-previous]');
      this.nextButton = section.querySelector('[data-slider-next]');
      this.progress = section.querySelector('[data-slider-progress]');
      this.progressBar = section.querySelector('[data-slider-progress-bar]');
      this.autoplayEnabled = section.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(section.dataset.autoplaySpeed) || 5000;
      this.currentIndex = 0;
      this.isIntersecting = true;
      this.isHovered = false;
      this.hasFocus = false;
      this.timer = null;
      this.scrollTimer = null;
      this.resumeTimer = null;
      this.resizeFrame = null;

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.onPrevious = () => this.goTo(this.currentIndex - 1, true);
      this.onNext = () => this.goTo(this.currentIndex + 1, true);
      this.onScroll = () => this.handleScroll();
      this.onResize = () => this.handleResize();
      this.onPointerEnter = () => {
        this.isHovered = true;
        this.stopAutoplay();
      };
      this.onPointerLeave = () => {
        this.isHovered = false;
        this.startAutoplay();
      };
      this.onPointerDown = () => this.stopAutoplay();
      this.onPointerUp = () => this.scheduleAutoplay();
      this.onFocusIn = () => {
        this.hasFocus = true;
        this.stopAutoplay();
      };
      this.onFocusOut = () => {
        window.setTimeout(() => {
          this.hasFocus = this.section.contains(document.activeElement);
          this.startAutoplay();
        }, 0);
      };
      this.onVisibilityChange = () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      };
      this.onBlockSelect = (event) => this.handleBlockSelect(event);

      this.bindEvents();
      this.observeVisibility();
      this.update();
      this.startAutoplay();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.section.addEventListener('pointerenter', this.onPointerEnter);
      this.section.addEventListener('pointerleave', this.onPointerLeave);
      this.section.addEventListener('focusin', this.onFocusIn);
      this.section.addEventListener('focusout', this.onFocusOut);
      this.section.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          this.isIntersecting = entries[0].isIntersecting;
          if (this.isIntersecting) this.startAutoplay();
          else this.stopAutoplay();
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this.section);
    }

    getVisibleCount() {
      const firstSlide = this.slides[0];
      if (!firstSlide) return 1;

      const slideWidth = firstSlide.getBoundingClientRect().width;
      const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      if (slideWidth <= 0) return 1;

      return Math.max(1, Math.min(this.slides.length, Math.round((this.viewport.clientWidth + gap) / (slideWidth + gap))));
    }

    getMaxIndex() {
      return Math.max(0, this.slides.length - this.getVisibleCount());
    }

    goTo(index, manual = false) {
      const maxIndex = this.getMaxIndex();
      this.currentIndex = Math.max(0, Math.min(index, maxIndex));
      const target = this.slides[this.currentIndex];
      if (!target) return;

      this.viewport.scrollTo({
        left: target.offsetLeft - this.track.offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.updateControls();

      if (manual) this.scheduleAutoplay();
    }

    handleScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const maxIndex = this.getMaxIndex();
        const trackOffset = this.track.offsetLeft;
        const closest = this.slides.reduce(
          (result, slide, index) => {
            const distance = Math.abs(this.viewport.scrollLeft - (slide.offsetLeft - trackOffset));
            return distance < result.distance ? { index, distance } : result;
          },
          { index: 0, distance: Number.POSITIVE_INFINITY }
        );
        this.currentIndex = Math.min(closest.index, maxIndex);
        this.updateControls();
      }, 100);
    }

    handleResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.currentIndex = Math.min(this.currentIndex, this.getMaxIndex());
        this.goTo(this.currentIndex);
        this.update();
      });
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest?.('[data-slider-slide]');
      if (!selectedSlide || !this.section.contains(selectedSlide)) return;

      const index = this.slides.indexOf(selectedSlide);
      if (index < 0) return;
      this.stopAutoplay();
      this.goTo(Math.min(index, this.getMaxIndex()));
    }

    updateControls() {
      const maxIndex = this.getMaxIndex();
      const progressSteps = maxIndex + 1;
      const progressValue = this.currentIndex + 1;
      const progressPercentage = (progressValue / progressSteps) * 100;

      if (this.controls) this.controls.hidden = maxIndex === 0;
      if (this.previousButton) this.previousButton.disabled = this.currentIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.currentIndex === maxIndex;
      if (this.progress) {
        this.progress.setAttribute('aria-valuemax', progressSteps);
        this.progress.setAttribute('aria-valuenow', progressValue);
      }
      if (this.progressBar) this.progressBar.style.setProperty('--grid-progress', `${progressPercentage}%`);
    }

    update() {
      const maxIndex = this.getMaxIndex();
      this.currentIndex = Math.min(this.currentIndex, maxIndex);
      this.section.classList.toggle('animated-card-grid--static', maxIndex === 0);
      this.updateControls();
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        !reduceMotion.matches &&
        !document.hidden &&
        this.isIntersecting &&
        !this.isHovered &&
        !this.hasFocus &&
        this.getMaxIndex() > 0
      );
    }

    startAutoplay() {
      if (this.timer || !this.canAutoplay()) return;
      this.timer = window.setInterval(() => {
        const maxIndex = this.getMaxIndex();
        this.goTo(this.currentIndex >= maxIndex ? 0 : this.currentIndex + 1);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    scheduleAutoplay() {
      this.stopAutoplay();
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.startAutoplay(), 1200);
    }

    destroy() {
      this.stopAutoplay();
      window.clearTimeout(this.scrollTimer);
      window.clearTimeout(this.resumeTimer);
      window.cancelAnimationFrame(this.resizeFrame);
      this.observer?.disconnect();
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.viewport.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.section.removeEventListener('pointerenter', this.onPointerEnter);
      this.section.removeEventListener('pointerleave', this.onPointerLeave);
      this.section.removeEventListener('focusin', this.onFocusIn);
      this.section.removeEventListener('focusout', this.onFocusOut);
      this.section.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  function init(root = document) {
    const sections = root.matches?.('[data-card-slider]') ? [root] : root.querySelectorAll('[data-card-slider]');
    sections.forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new CardSlider(section));
    });
  }

  function destroy(root) {
    const sections = root.matches?.('[data-card-slider]') ? [root] : root.querySelectorAll('[data-card-slider]');
    sections.forEach((section) => {
      instances.get(section)?.destroy();
      instances.delete(section);
    });
  }

  window.DaramelCardSlider = { init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
